<script setup>
import { ref, watch } from 'vue';
import { ACCOUNTING, SERVICE } from '@/api';
import { useToast } from 'vue-toastification';
import BaseAccounting from './BaseAccounting.vue';
import AccountingWithTrip from './AccountingWithTrip.vue';

const toast = useToast();

const formComponent = ref(BaseAccounting);

const props = defineProps({
  accounting: {
    type: Object || null,
    default: {},
  },
  isProject: {
    type: Boolean,
    default: false,
  },
  projectId: {
    type: Number || null,
  },
  units: {
    type: Array,
    default: [],
  },
  projectType: {
    type: String,
  },
  accountingCompanies: {
    type: Array,
    default: [],
  },
});

const buttonText = ref('Create');
const errors = ref([]);
const invoiceRequired = ref('required');
const accountingCompanyOptions = ref([]);
const form = ref();
const isSubmitting = ref(false);
const emit = defineEmits(['close', 'refresh']);

function closeModal() {
  if (isSubmitting.value) {
    toast.warning('Please wait for the upload to complete');
    return;
  }
  buttonText.value = 'Create';
  form.value?.resetSubmitting();
  form.value?.resetFormData();
  invoiceRequired.value = 'required';
  emit('close');
}

const isDisabled = status => {
  const disabledStatus = ['waiting', 'paid', 'approve'];
  return disabledStatus.includes(status);
};

/**
 * 使用 presigned URL 上傳檔案
 * @param {File} file - 檔案物件
 * @param {string} destPath - 目標路徑 (e.g. 'invoice', 'attachments')
 * @returns {Promise<{path: string, url: string, originalName: string}>}
 */
const fileUpload = async (file, destPath) => {
  return await SERVICE.uploadWithSignedUrl(file, destPath);
};

const actionSwitch = async (actionName, formData) => {
  switch (actionName) {
    case 'save':
      await ACCOUNTING.update(props.accounting?.id, formData);
      break;
    case 'delete':
      await ACCOUNTING.delete(props.accounting?.id);
      break;
    case 'submit':
      await ACCOUNTING.submit(props.accounting?.id, formData);
  }
};

const submitHandler = async (formData, action) => {
  const newData = JSON.parse(JSON.stringify(formData));
  errors.value = [];
  isSubmitting.value = true;
  newData['project_type'] = props.projectType;

  try {
    if (props.projectId) {
      newData['project_id'] = props.projectId;
    }
    if (formData.invoice?.[0]?.file instanceof File) {
      const result = await fileUpload(formData.invoice[0].file, 'invoice');
      newData.invoice = result.name;
    } else {
      delete newData['invoice'];
    }
    if (formData.attachments?.[0]?.file instanceof File) {
      const attachments = [];
      for (const attachment of formData.attachments) {
        const result = await fileUpload(attachment.file, 'attachments');
        attachments.push({
          originalName: result.originalName,
          name: result.name,
          url: result.url,
        });
      }
      newData.attachments = attachments;
    } else {
      delete newData['attachments'];
    }

    if (props.accounting?.id) {
      await actionSwitch(action, newData);
      toast.success(`${action} accounting successfully`);
    } else {
      await ACCOUNTING.post(newData);
      toast.success(`created successfully`);
    }

    const type = props?.projectId ? 'project' : 'accounting';
    emit('refresh', type);
    isSubmitting.value = false;
    closeModal();
  } catch (e) {
    isSubmitting.value = false;
    form.value?.resetSubmitting();

    if (e.response?.data?.errors) {
      Object.keys(e.response.data.errors).forEach(key => {
        errors.value.push(e.response.data.errors[key].join('.'));
      });
    } else {
      toast.error(e.message || 'Operation failed, please try again');
    }
  }
};

defineExpose({
  isSubmitting,
});

watch(
  () => props.accounting,
  value => {
    if (Object.keys(value).length) {
      buttonText.value = 'Update';
      invoiceRequired.value = 'nullable';
    }
  },
);

watch(
  () => props.isProject,
  value => {
    formComponent.value = value ? AccountingWithTrip : BaseAccounting;
  },
);

watch(
  () => props.accountingCompanies,
  companies => {
    accountingCompanyOptions.value = companies.map(company => ({
      label: company.company_name,
      value: company.id,
      is_current: company.is_current,
    }));
  },
);
</script>

<template>
  <div class="bg-white w-[90vw] max-w-sm rounded max-h-[95vh] relative">
    <!-- Processing 遮罩 -->
    <div
      v-if="isSubmitting"
      class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded z-50"
    >
      <div class="bg-white px-6 py-4 rounded shadow-lg">
        <div
          class="w-12 h-12 border-4 border-indigo-400 rounded-full border-t-transparent animate-spin mx-auto mb-3"
        ></div>
        <div class="text-center font-medium">Uploading files...</div>
        <div class="text-center text-sm text-gray-600 mt-1">Please wait</div>
      </div>
    </div>

    <div class="flex justify-between px-4 py-2">
      <div class="px-2 py-1 bg-gray-200 rounded shadow w-fit">
        {{ accounting?.id ? 'update' : 'create' }}
      </div>
      <button
        @click="closeModal"
        :disabled="isSubmitting"
        :class="[
          'transition-opacity',
          isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-70',
        ]"
      >
        ❌
      </button>
    </div>
    <div class="overflow-y-auto max-h-[80vh] px-4 py-2">
      <div class="px-2 mb-4 bg-gray-200 rounded" v-if="accounting.remark">
        <div class="font-bold">Incomplete reason</div>
        <div class="text-rose-700">{{ accounting.remark }}</div>
      </div>
      <component
        :is="formComponent"
        :units="props.units"
        :disabled="isDisabled(accounting.status)"
        ref="form"
        :buttonText="buttonText"
        :invoiceRequired="invoiceRequired"
        :accounting="accounting"
        :accountingCompanyOptions="accountingCompanyOptions"
        @submitHandler="submitHandler"
      />
    </div>
  </div>
</template>
